import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { bookingsApi } from '@/api/client'
import { setI18nLanguage } from '@/i18n'

import { useReservationStore } from './reservation'
import type { Config } from './types'

export const useConfigStore = defineStore('config', () => {
  const config = ref<Config>({
    warranty: {
      method: 'ALL',
      type: 'NONE',
      amount: 0
    },
    cancellation: {
      time: 1,
      unit: 'days'
    },
    largeGroupMin: 0,
    timezone: 'Europe/Madrid',
    locationAddress: '',
    locationPhoneNumber: '',
    language: 'en',
    scripts: {},
    dinersLimit: 12,
    customPage: true
  })

  const bookingStore = useReservationStore()
  const router = useRouter()
  const { bookingId, diners } = storeToRefs(bookingStore)

  const hasWarranty = computed(() => {
    const { warranty, largeGroupMin } = config.value
    if (warranty.type === 'NONE' || bookingId.value) return false

    if (warranty.method === 'LARGE-GROUPS') {
      return diners.value && largeGroupMin && diners.value >= largeGroupMin
    }
    return true
  })

  async function getLocationConfig() {
    try {
      const response = await bookingsApi.getConfig()
      if (!response.customPage) router.push({ name: '404' })
      config.value = response
      setI18nLanguage(response.language || window.navigator.language)
    } catch {
      router.push({ name: '404' })
    }
  }

  return {
    warranty: computed(() => config.value.warranty),
    cancellation: computed(() => config.value.cancellation),
    largeGroupMin: computed(() => config.value.largeGroupMin),
    brandInfo: computed(() => config.value.brandInfo),
    timezone: computed(() => config.value.timezone),
    locationAddress: computed(() => config.value.locationAddress),
    locationPhoneNumber: computed(() => config.value.locationPhoneNumber),
    language: computed(() => config.value.language),
    scripts: computed(() => config.value.scripts),
    dinersLimit: computed(() => config.value.dinersLimit),
    extraText: computed(() => config.value.extraText),
    hasWarranty,
    getLocationConfig,
    config
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConfigStore, import.meta.hot))
}
